import { X, Crown, Check, Sparkles } from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ErnieProUpgradeModal({ onClose, monthlyPrice = 5.99 }) {
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/ernie-pro/create-checkout`,
        {},
        { withCredentials: true }
      );

      if (response.data.requires_payment === false) {
        // Free mode - no payment needed
        toast.success('✓ Ernie AI Pro activated!');
        window.location.reload();
      } else {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start upgrade process');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4">
      <div className="bg-white max-w-2xl w-full rounded-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-yellow-500 to-yellow-400 text-yellow-900 p-6 flex items-center justify-between">
          <h2 className="text-3xl font-bold flex items-center gap-3">
            <Crown className="w-8 h-8" />
            Upgrade to Ernie AI Pro
          </h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-yellow-900 hover:text-yellow-800 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Price */}
          <div className="text-center py-6 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg border-2 border-yellow-400">
            <div className="text-5xl font-bold text-yellow-900">
              ${monthlyPrice}
              <span className="text-2xl font-normal">/month</span>
            </div>
            <p className="text-yellow-800 mt-2">Cancel anytime</p>
          </div>

          {/* Features */}
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-500" />
              What you get with Pro:
            </h3>
            
            <div className="space-y-3">
              {[
                'Ask about ANY topic - unlimited subjects',
                'Full AI model capabilities (coding, writing, analysis)',
                'Personal assistant for work, school, or life',
                'Business advice, creative writing, problem solving',
                'Math, science, history - everything!',
                'PLUS all TCM & acupuncture expertise (always included)',
              ].map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-800">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Comparison */}
          <div className="border-t-2 border-gray-200 pt-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Free vs Pro:</h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              {/* Free */}
              <div className="p-4 bg-gray-100 rounded-lg border-2 border-gray-300">
                <h4 className="font-bold text-gray-700 mb-2">FREE</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>✓ TCM questions</li>
                  <li>✓ Acupuncture guidance</li>
                  <li>✓ Chinese medicine</li>
                  <li className="text-red-600">✗ Other topics limited</li>
                </ul>
              </div>

              {/* Pro */}
              <div className="p-4 bg-gradient-to-br from-yellow-100 to-yellow-50 rounded-lg border-2 border-yellow-400">
                <h4 className="font-bold text-yellow-900 mb-2 flex items-center gap-2">
                  <Crown className="w-4 h-4" />
                  PRO
                </h4>
                <ul className="space-y-2 text-sm text-yellow-900">
                  <li>✓ TCM questions</li>
                  <li>✓ Acupuncture guidance</li>
                  <li>✓ Chinese medicine</li>
                  <li className="font-bold text-green-700">✓ UNLIMITED topics!</li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={handleUpgrade}
              disabled={loading}
              className="flex-1 px-6 py-4 bg-gradient-to-r from-yellow-500 to-yellow-400 text-yellow-900 font-bold text-lg rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-900"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Crown className="w-6 h-6" />
                  Upgrade Now
                </>
              )}
            </button>
            <button
              onClick={onClose}
              disabled={loading}
              className="px-6 py-4 bg-gray-200 text-gray-800 font-bold rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
